<template>
  <div class="invoice-content">
    <div class="header-actions">
      <div class="search-container">
        <input type="text" placeholder="Search invoices by number or customer" v-model="searchQuery"
          class="search-input" />
      </div>
    </div>

    <div class="table-container">
      <SkeletonTableLoader v-if="loading" :rowCount="8" :columnCount="7" />

      <div v-else-if="!loading && filteredInvoices.length === 0" class="no-data-container">
        <p class="no-data-text">No invoices found. Try adjusting your search.</p>
      </div>

      <table v-else-if="!loading && filteredInvoices.length > 0">
        <thead>
          <tr>
            <th>Invoice #</th>
            <th>Invoice Date</th>
            <th>Due Date</th>
            <th>Grand Total</th>
            <th>Outstanding Amount</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="invoice in filteredInvoices" :key="invoice.id">
            <td>{{ invoice.id }}</td>
            <td>{{ formatDate(invoice.invoiceDate) }}</td>
            <td>{{ formatDate(invoice.dueDate) }}</td>
            <td>
              {{ invoice.currency }} {{ Number(invoice.grandTotal || 0).toLocaleString() }}
            </td>
            <td>
              {{ invoice.currency }} {{ Number(invoice.outstandingAmount || 0).toLocaleString() }}
            </td>
            <td>
              <span :class="getStatusClass(invoice.status)">
                {{ invoice.status || 'Unknown' }}
              </span>
            </td>
            <td class="actions-cell">
              <div class="actions-dropdown">
                <button class="actions-btn" @click="toggleActionMenu(invoice.id)">
                  ACTIONS
                  <i class="fas fa-chevron-down" />
                </button>

                <div v-if="activeActionMenus.includes(invoice.id)" class="custom-dropdown-menu">
                  <button v-if="canExportInvoice" class="action-option" :disabled="isDownloading(invoice.id)"
                    @click="download(invoice)">
                    <span v-if="isDownloading(invoice.id)" class="loading-inline">
                      <i class="fas fa-spinner fa-spin"></i>
                      Downloading...
                    </span>
                    <span v-else class="inline-flex">
                      <i class="fas fa-file-download"></i>
                      Download
                    </span>
                  </button>

                  <button class="action-option" @click="viewAttachments(invoice)">
                    <i class="fas fa-paperclip" />
                    Attachments
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <TenantInvoiceAttachmentsModal 
    :visible="showAttachments" 
    :invoice="selectedInvoice" 
    @close="closeAttachments()" />
  </div>
</template>

<script>
import TenantInvoiceAttachmentsModal from '../../components/Admin/TenantBilling/TenantInvoiceAttachmentsModal.vue';
import SkeletonTableLoader from '../../components/Shared/SkeletonTableLoader.vue';
import { mapActions, mapState, mapGetters } from 'vuex';
import { formatToDDMMMYY_HHmm } from "../../helpers/FormatHelpers";

export default {
  name: 'TenantBillingManagement',
  components: {
    SkeletonTableLoader,
    TenantInvoiceAttachmentsModal,
  },
  data() {
    return {
      searchQuery: '',
      showAttachments: false,
      selectedInvoice: null,
      activeActionMenus: [],
      downloadingInvoiceIds: [],
    };
  },
  computed: {
    ...mapState('tenantBilling', ['invoices', 'loading']),
    ...mapGetters('tenantBilling', ['billingConfiguration']),

    filteredInvoices() {
      if (!this.searchQuery) return Object.values(this.invoices || {});

      const query = this.searchQuery.toLowerCase().trim();

      return Object.values(this.invoices || {}).filter(inv => {
        return (
          (inv.id || '').toLowerCase().includes(query) ||
          (inv.customer || '').toLowerCase().includes(query) ||
          (inv.status || '').toLowerCase().includes(query)
        );
      });
    },

    canExportInvoice() {
      return !!this.billingConfiguration?.invoiceExportFormat;
    },
  },
  methods: {
    ...mapActions('tenantBilling', ['fetchInvoices', 'downloadInvoice']),

    async loadInvoices() {
      try {
        await this.fetchInvoices();
      } catch (error) {
        this.$toasted.error(error.message || 'Failed to load invoices', {
          icon: 'exclamation-triangle',
          theme: 'outline',
          position: 'bottom-center',
          singleton: true,
          duration: 2000,
        });
      }
    },

    toggleActionMenu(invoiceId) {
      if (this.activeActionMenus.includes(invoiceId)) {
        this.activeActionMenus = this.activeActionMenus.filter(id => id !== invoiceId);
      } else {
        this.activeActionMenus = [invoiceId];
      }
    },

    viewAttachments(invoice) {
      this.showAttachments = true;
      this.selectedInvoice = invoice;
    },

    closeAttachments() {
      this.showAttachments = false;
      this.selectedInvoice = null;
    },

    isDownloading(invoiceId) {
      return this.downloadingInvoiceIds.includes(invoiceId);
    },

    async download(invoice) {
      const invoiceId = invoice.id;

      if (this.isDownloading(invoiceId)) return;

      this.downloadingInvoiceIds.push(invoiceId);

      try {
        await this.downloadInvoice({ invoiceId });
      } catch (error) {
        this.$toasted.error(error.message || 'Failed to download invoice', {
          icon: 'exclamation-triangle',
          theme: 'outline',
          position: 'bottom-center',
          singleton: true,
          duration: 2000,
        });
      } finally {
        this.downloadingInvoiceIds = this.downloadingInvoiceIds.filter(id => id !== invoiceId);
        this.activeActionMenus = this.activeActionMenus.filter(id => id !== invoiceId);
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '—';
      return formatToDDMMMYY_HHmm(dateStr);
    },

    getStatusClass(status) {
      if (!status) return '';
      const s = status.toLowerCase();
      if (s === 'overdue') return 'status-overdue';
      if (s === 'paid') return 'status-paid';
      if (s === 'pending' || s === 'open') return 'status-pending';
      return 'status-default';
    },
  },
  mounted() {
    this.loadInvoices();
  },
};
</script>

<style scoped>
.invoice-content {
  height: calc(100svh - var(--header-height) - 3rem);
  padding: 1.5rem;
  overflow: auto;
}

.header-actions {
  display: flex;
  margin-bottom: 1.5rem;
  justify-content: flex-start;
}

.search-container {
  flex: 1;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.table-container {
  margin-bottom: 1.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th,
td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

th {
  background-color: #f9fafb;
  font-weight: 500;
  color: #374151;
}

.actions-dropdown {
  position: relative;
}

.actions-btn {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0 0.75rem;
  height: 27px;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background-color: #fff;
  font-size: 1rem;
  cursor: pointer;
  white-space: nowrap;
}

.custom-dropdown-menu {
  position: absolute;
  left: 0;
  top: 100%;
  margin-top: 0.25rem;
  background-color: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 999;
  width: 100%;
  min-width: 140px;
}

.action-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 1rem;
  text-align: left;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.95rem;
}

.action-option:hover {
  background-color: #f9fafb;
}

.action-option:disabled,
.action-option:disabled:hover {
  opacity: 0.6;
  cursor: not-allowed;
  background: none;
}

.inline-flex, .loading-inline {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.loading-inline {
  color: #6b7280;
}

.no-data-container {
  padding: 3rem 1rem;
  text-align: center;
  color: #6b7280;
}

.status-overdue {
  color: #dc2626;
  font-weight: 500;
}

.status-paid {
  color: #4BB543;
  font-weight: 500;
}

.status-pending {
  color: #d97706;
  font-weight: 500;
}

.status-default {
  color: #6b7280;
}
</style>